import { computed, getCurrentScope, onScopeDispose, watch, type ComputedRef } from 'vue'

import { platformActionApi } from '../api/PlatformActionApi'
import { DecideApprovalFlow } from '../flows/DecideApprovalFlow'
import { LoadApprovalsFlow } from '../flows/LoadApprovalsFlow'
import type { ApprovalInboxRow, ApprovalState, ApprovalVerb } from '../models/platformAction.types'
import type { PlatformActionError } from '../models/platformActionError'
import { toFirst, toPageParams } from '../service/pageParams'
import { useApprovalInboxStore } from '../stores/approvalInboxStore'

import { useWorkspace } from '@/features/account'
import type { UiAsyncState } from '@/shared/ui'

/**
 * The inbox races other surfaces for the same state — a decision can be recorded from chat or
 * Slack while the page is open — so it re-reads on an interval and when the tab returns to the
 * foreground rather than trusting what it rendered.
 */
export const INBOX_REFRESH_MS = 30_000

interface InboxFlow {
  execute: LoadApprovalsFlow['execute']
  count: LoadApprovalsFlow['count']
}

interface DecideFlow {
  execute: DecideApprovalFlow['execute']
}

/** Internal injection seam: production callers use the no-argument contract. */
export interface UseApprovalInboxOptions {
  flow?: InboxFlow
  decideFlow?: DecideFlow
  /** `false` turns the background refresh off — the "All changes" tab never polls. */
  poll?: ComputedRef<boolean>
  intervalMs?: number
  /** Injected so specs drive the interval with fake timers instead of real time. */
  schedule?: (callback: () => void, ms: number) => number
  cancel?: (handle: number) => void
}

export interface UseApprovalInbox {
  state: ComputedRef<UiAsyncState>
  error: ComputedRef<PlatformActionError | null>
  rows: ComputedRef<ApprovalInboxRow[]>
  stateFilter: ComputedRef<ApprovalState | ''>
  isFilteredEmpty: ComputedRef<boolean>
  scanTruncated: ComputedRef<boolean>
  awaitingDecision: ComputedRef<number>
  first: ComputedRef<number>
  pageRows: ComputedRef<number>
  total: ComputedRef<number>
  setStateFilter: (next: ApprovalState | '') => void
  setPagination: (change: { first: number; rows: number }) => void
  reload: () => Promise<void>
  /** Re-read without the loading state — what is on screen stays until the fresh page lands. */
  refresh: () => Promise<void>
  /** Record a verb, then re-read. Resolves to the refusal on failure, `null` on success. */
  decide: (
    row: ApprovalInboxRow,
    verb: ApprovalVerb,
    comment?: string,
  ) => Promise<PlatformActionError | null>
}

export function useApprovalInbox(options: UseApprovalInboxOptions = {}): UseApprovalInbox {
  const workspace = useWorkspace()
  const store = useApprovalInboxStore()
  const flow = options.flow ?? new LoadApprovalsFlow({ api: platformActionApi, store })
  const decideFlow = options.decideFlow ?? new DecideApprovalFlow({ api: platformActionApi })
  const intervalMs = options.intervalMs ?? INBOX_REFRESH_MS
  const schedule = options.schedule ?? ((callback, ms) => window.setInterval(callback, ms))
  const cancel = options.cancel ?? ((handle) => window.clearInterval(handle))

  const workspaceId = computed(() => numericId(workspace.workspaceId.value))

  function query(): { state: string; page: number; pageSize: number } {
    return { state: store.stateFilter, page: store.page, pageSize: store.pageSize }
  }

  async function load(silent = false): Promise<void> {
    const id = workspaceId.value
    if (id === null) return
    await Promise.all([flow.execute(id, query(), { silent }), flow.count(id)])
  }

  watch(
    () => [workspaceId.value, store.stateFilter, store.page, store.pageSize] as const,
    () => {
      void load()
    },
    { immediate: true },
  )

  let handle: number | null = null

  function stopTimer(): void {
    if (handle !== null) cancel(handle)
    handle = null
  }

  function isHidden(): boolean {
    return typeof document !== 'undefined' && document.hidden
  }

  function startTimer(): void {
    stopTimer()
    if (options.poll && !options.poll.value) return
    handle = schedule(() => {
      if (!isHidden()) void load(true)
    }, intervalMs)
  }

  function onVisibility(): void {
    if (isHidden()) return
    if (options.poll && !options.poll.value) return
    void load(true)
  }

  startTimer()
  if (options.poll) watch(options.poll, startTimer)
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility)

  if (getCurrentScope()) {
    onScopeDispose(() => {
      stopTimer()
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility)
      }
    })
  }

  async function decide(
    row: ApprovalInboxRow,
    verb: ApprovalVerb,
    comment?: string,
  ): Promise<PlatformActionError | null> {
    const id = workspaceId.value
    if (id === null) return null
    const result = await decideFlow.execute(id, row.request.id, verb, comment)
    // A refused decision changed nothing, but the row may have moved under us either way.
    await load(true)
    return result.ok ? null : result.error
  }

  return {
    state: computed(() => asyncState(store.status, store.rows.length)),
    error: computed(() => store.error),
    rows: computed(() => store.rows),
    stateFilter: computed(() => store.stateFilter),
    isFilteredEmpty: computed(() => store.isFilteredEmpty),
    scanTruncated: computed(() => store.scanTruncated),
    awaitingDecision: computed(() => store.awaitingDecision),
    first: computed(() => toFirst(store.page, store.pageSize)),
    pageRows: computed(() => store.pageSize),
    total: computed(() => store.total),
    setStateFilter: (next) => store.setStateFilter(next),
    setPagination: ({ first, rows }) => {
      const params = toPageParams(first, rows)
      if (params.page_size !== store.pageSize) store.setPageSize(params.page_size)
      else if (params.page !== store.page) store.setPageNumber(params.page)
    },
    reload: () => load(),
    refresh: () => load(true),
    decide,
  }
}

function asyncState(status: string, count: number): UiAsyncState {
  if (status === 'loading' || status === 'idle') return 'loading'
  if (status === 'error') return 'error'
  return count === 0 ? 'empty' : 'ready'
}

function numericId(value: string | number | undefined): number | null {
  if (value === undefined || (typeof value === 'string' && value.trim() === '')) return null
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}
